import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Utils, fetchWithAuth, getSubscriptionDisplayStatus, formatDateTime } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FiCalendar, FiChevronDown } from "react-icons/fi";
import './SubscriptionProfile.css';

const SubscriptionProfile = () => {
    const { appId, subscriptionId } = useParams();
    const navigate = useNavigate();
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDays, setSelectedDays] = useState(3);
    const [activityData, setActivityData] = useState([]);
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [expandedActivity, setExpandedActivity] = useState(null);

    const fetchSubscriptionProfile = useCallback(async () => {
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'get/subscription/profile', {
                method: 'POST',
                body: JSON.stringify({ id: subscriptionId }),
            });
            const result = await response.json();
            if (result.status) {
                setSubscriptionData(result.data);
            }
        } catch (error) {
            console.error('Error fetching subscription profile:', error);
        } finally {
            setIsLoading(false);
        }
    }, [subscriptionId]);

    const fetchActivityTimeline = useCallback(async () => {
        if (!subscriptionData?.subscription_id) return;

        setIsLoadingActivity(true);
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'get/subscription/activity', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ subscription_id: subscriptionData.subscription_id, days: selectedDays }),
            });
            const result = await response.json();
            if (result.status) {
                setActivityData(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching activity timeline:', error);
        } finally {
            setIsLoadingActivity(false);
        }
    }, [subscriptionData?.subscription_id, selectedDays]);

    useEffect(() => {
        fetchSubscriptionProfile();
    }, [fetchSubscriptionProfile]);

    useEffect(() => {
        if (subscriptionData?.subscription_id) {
            fetchActivityTimeline();
        }
    }, [subscriptionData, selectedDays, fetchActivityTimeline]);

    const toggleActivityExpand = (activityId) => {
        setExpandedActivity(expandedActivity === activityId ? null : activityId);
    };

    const formatActivityDate = (dateString) => {
        if (!dateString) return 'N/A';
        return formatDateTime(dateString, { seconds: true }) || 'N/A';
    };

    const getActivityStatus = (activity) => {
        if (activity.onPressStatus === 1) {
            return 'Push Notification Clicked';
        } else if (activity.sentStatus === 1) {
            return 'Push Notification Received';
        } else {
            return 'Push Notification Sent';
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard_right">
                <div className="dashboard_inner">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    </div>
                </div>
            </div>
        );
    }

    if (!subscriptionData) {
        return (
            <div className="dashboard_right">
                <div className="dashboard_inner">
                    <p>Subscription not found</p>
                </div>
            </div>
        );
    }

    const statusUi = getSubscriptionDisplayStatus(subscriptionData.status);

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="subscription-profile-header">
                    <h1>Subscription ID: {subscriptionData.subscription_id}</h1>
                    <button className="actions-button">
                        Actions
                        <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '12px', height: '12px', marginLeft: '5px' }}>
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                        </svg>
                    </button>
                </div>

                <div className="subscription-profile-content">
                    {/* Left Column - Profile */}
                    <div className="profile-section">
                        <h3>Subscription Details</h3>

                        {/* Device Information */}
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M4 2h8v12H4V2zm2 1v10h4V3H6z" />
                            </svg>
                            <span>Device: {subscriptionData.device_model || 'Unknown'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                            </svg>
                            <span>OS: {subscriptionData.device_os || 'Unknown'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M2 3h12v2H2V3zm0 4h12v2H2V7z" />
                            </svg>
                            <span>App Version: {subscriptionData.app_version || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                            </svg>
                            <span>SDK: {subscriptionData.sdk || 'N/A'}</span>
                        </div>

                        {/* Status Information */}
                        <div className="status-section">
                            <h3>Status</h3>
                            <div className="status-badge">
                                {statusUi.badgeClass ? (
                                    <span className={statusUi.badgeClass}>{statusUi.label}</span>
                                ) : (
                                    <span>{statusUi.label}</span>
                                )}
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Status: {statusUi.label}</span>
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Type: {subscriptionData.type || 'AndroidPush'}</span>
                            </div>
                        </div>

                        {/* Location & Settings */}
                        <div className="location-section">
                            <h3>Location & Settings</h3>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Language: {subscriptionData.language || 'en'}</span>
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Timezone: {subscriptionData.timezone_id || 'N/A'}</span>
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Carrier: {subscriptionData.carrier || 'N/A'}</span>
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Network Type: {subscriptionData.net_type || 'N/A'}</span>
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Rooted: {subscriptionData.rooted ? 'Yes' : 'No'}</span>
                            </div>
                        </div>

                        {/* IDs Section */}
                        <div className="ids-section">
                            <h3>IDs</h3>
                            <div className="id-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Subscription ID: {subscriptionData.subscription_id}</span>
                                <button className="copy-button">
                                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                        <path d="M4 2h8v2H4V2zm0 4h8v2H4V6zm0 4h5v2H4v-2z" />
                                    </svg>
                                </button>
                            </div>
                            {subscriptionData.app_user && (
                                <div className="id-item">
                                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                        <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                    </svg>
                                    <span>Notify Sphere ID: {subscriptionData.app_user.notify_sphere_id}</span>
                                    <button
                                        className="link-button"
                                        onClick={() => navigate(`/apps/${appId}/users/${subscriptionData.app_user.id}`)}
                                    >
                                        View User
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Push Token Section */}
                        <div className="token-section">
                            <h3>Push Token</h3>
                            <div className="token-display">
                                <code>{subscriptionData.token || 'No token available'}</code>
                                <button className="copy-button">
                                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                        <path d="M4 2h8v2H4V2zm0 4h8v2H4V6zm0 4h5v2H4v-2z" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Session Information */}
                        <div className="session-section">
                            <h3>Session Information</h3>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Created: {formatDateTime(subscriptionData.createdAt)}</span>
                            </div>
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>Last Updated: {formatDateTime(subscriptionData.updatedAt)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Activity Timeline */}
                    <div className="activity-section">
                        <h3>Activity Timeline</h3>
                        <p className="activity-subtitle">Tracked up to {selectedDays} days</p>
                        <div className="days-selector">
                            <button className={selectedDays === 3 ? 'active' : ''} onClick={() => setSelectedDays(3)}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <FiCalendar size={16} />
                                    <span>3 days</span>
                                    <FiChevronDown size={16} />
                                </span>
                            </button>
                        </div>

                        {isLoadingActivity ? (
                            <div style={{ textAlign: 'center', padding: '30px' }}>
                                <FontAwesomeIcon icon={faSpinner} spin size="lg" />
                            </div>
                        ) : activityData.length > 0 ? (
                            <div className="activity-timeline">
                                {activityData.map((activity) => (
                                    <div key={activity.id} className="activity-item">
                                        <div
                                            className="activity-item-header"
                                            onClick={() => toggleActivityExpand(activity.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <svg
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    transform: expandedActivity === activity.id ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s'
                                                }}
                                            >
                                                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" />
                                            </svg>
                                            <span className="activity-timestamp">{formatActivityDate(activity.timestamp)}</span>
                                            <span className="activity-status">{getActivityStatus(activity)}</span>
                                        </div>

                                        {expandedActivity === activity.id && activity.notification && (
                                            <div className="activity-details">
                                                <h4>Message Details</h4>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Name</span>
                                                    <span className="detail-value">{activity.notification_id || 'N/A'}</span>
                                                </div>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Sent</span>
                                                    <span className="detail-value">{formatActivityDate(activity.notification.sent_at)}</span>
                                                </div>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Message Title</span>
                                                    <span className="detail-value">{activity.notification.title || 'N/A'}</span>
                                                </div>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Message Content</span>
                                                    <span className="detail-value">{activity.notification.message || 'N/A'}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-activity">
                                <div className="no-activity-icon">
                                    <svg viewBox="0 0 100 100" fill="none" style={{ width: '80px', height: '80px' }}>
                                        <circle cx="50" cy="50" r="40" fill="#e8eaed" />
                                        <path d="M50 30v20M50 55v5" stroke="#5f6368" strokeWidth="4" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <h4>No Recent Activity</h4>
                                <p>You'll see data here when there's subscription activity.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionProfile;
